/**
 * Two-model modes (ported from Blank #258/#272) — generic, host-agnostic.
 *
 * DELEGATE `[planner->worker]` — the (premium) agent model PLANS, a cheaper worker model WRITES.
 * The planner keeps every read tool plus `Task`/`TaskBatch`, but may not write: a nudge in the prompt
 * was not enough (Opus, told to delegate, wrote the whole app itself 8 runs of 8). So the rule is
 * enforced at EXECUTION. Workers inherit this exact toolbelt (children get the parent's tools), so the
 * one thing that tells a planner call from a worker call is the calling agent's own model, which the
 * Agent puts on `ToolContext.model`. That is why delegate mode refuses to start when the models are equal.
 *
 * ADVISOR `[worker<-advisor]` — the inverse. The cheap model drives with every tool; `Advise` makes a
 * few short calls to the premium model that see only the request, an optional host snapshot and the
 * question — never the system prompt or the conversation (Anthropic's advisor tool / Aider's architect).
 * A prompt rule was not enough here either (Haiku, told to consult, wrote everything with 0 calls), so
 * the first write waits for one consult.
 *
 * Both modes run the second model through the agent's shared `ai`; `meterWorkers` sums every call on
 * a model other than the agent's own, which the Agent reports as `RunResult.workerUsage`.
 */
import type { ChatLike } from './llm';
import { forComponent } from './logging';

const log = forComponent('modes');

/** Tools that change files — refused for the planner, gated for an advised worker. */
export const WRITE_TOOLS = new Set(['Write', 'Edit', 'MultiEdit', 'ApplyEdits']);
/** The planner also loses the shells: either one can write files. */
export const PLANNER_BLOCKED = new Set([...WRITE_TOOLS, 'bash', 'Shell']);

const lower = (xs: Set<string>) => new Set([...xs].map((x) => x.toLowerCase()));
const PLANNER_BLOCKED_LC = lower(PLANNER_BLOCKED), WRITE_TOOLS_LC = lower(WRITE_TOOLS);
/** Does the planner lose this tool? By the tool's own `writes` flag first — it survives a host rename (shraga-ee's
 *  `Shell` → `Bash`) — then by name, case-insensitively (`Bash`, a host's own `write`). */
export const plannerBlocks = (t: { name: string; writes?: boolean }) => !!t.writes || PLANNER_BLOCKED_LC.has(t.name.toLowerCase());
/** Is this a FILE write (the advisor gate)? Shells are not gated there: the worker may run tests before consulting. */
const isFileWrite = (t: { name: string }) => WRITE_TOOLS_LC.has(t.name.toLowerCase());

export const PLANNER_REFUSAL = 'Refused: you are the planner and do not write files. Delegate the change with Task/TaskBatch (model set to the worker) and give the worker a full spec.';
export const PLAN_FIRST = 'Refused: check your plan with `Advise` before the first write (files, data shape, the hard parts). Then write.';

/** A build may consult at most this many times by default: plan + review + one spare. */
export const ADVISE_LIMIT = 3;
/** The snapshot the advisor sees is cut at this size, so one call stays short. */
const SNAPSHOT_CAP = 60_000;

export type ModeTool = { name: string; run: (...a: any[]) => any };

/** Cursor is excluded on both sides: a cursor agent uses its NATIVE file tools in its own workspace,
 *  so neither the refusal nor the gate would apply to it. */
function pairOk(kind: string, main: string, other: string): boolean {
  if (other === main) { log.warn(`${kind} off: both sides are ${main}`); return false; }
  if (main.startsWith('cursor/') || other.startsWith('cursor/')) { log.warn(`${kind} off: cursor models are not supported (${main} / ${other})`); return false; }
  return true;
}

/** The worker model when delegate mode can run for this planner, else undefined (why: in the log). */
export function delegateWorkerFor(planner: string, worker?: string | false | null): string | undefined {
  return worker && pairOk('delegate', planner, worker) ? worker : undefined;
}

/** The advisor model when advisor mode can run for this worker, else undefined. */
export function advisorFor(worker: string, advisor?: string | false | null): string | undefined {
  return advisor && pairOk('advisor', worker, advisor) ? advisor : undefined;
}

/** Refuse the planner's file-changing tools; a worker (any other `ctx.model`) runs them as usual. */
export function delegateTools<T extends ModeTool>(tools: T[], planner: string): T[] {
  return tools.map((t) => !plannerBlocks(t) ? t : {
    ...t,
    run: async (...a: any[]) => (a[1]?.model === planner ? PLANNER_REFUSAL : t.run(...a)),
  });
}

/** Appended to the PLANNER's system prompt only (workers inherit the host's base prompt). */
export function delegatePrompt(worker: string): string {
  return `\n\n## You are the PLANNER\nYou cannot write files: Write, Edit, MultiEdit, ApplyEdits and the shells are refused for you. Every file change goes through a worker. Read what you need, decide the design (files, data shape, interfaces, the rules that matter), then hand the writing to workers with \`TaskBatch\` (independent files in parallel) or \`Task\`, with \`model: "${worker}"\` (the default). Give each worker a complete, self-contained spec: it sees the files but not your reasoning. When they finish, read the result and send fix-up tasks for anything wrong or missing. Finish only when the work is complete.`;
}

/** Appended to the advised worker's system prompt. */
export function advisorPrompt(advisor: string): string {
  return `\n\n## You have an ADVISOR\nThe \`Advise\` tool asks a stronger model (${advisor}). You do the work yourself. Call \`Advise\` once BEFORE you write — writes are refused until you do (ask it to check your plan: files, data shape, the hard parts) — and once AFTER the work is complete (ask it to review for defects), then fix what it finds. Do not call it for small steps.`;
}

async function textOf(r: any): Promise<string> {
  if (r && typeof r[Symbol.asyncIterator] === 'function') {
    let s = '';
    for await (const c of r) if (typeof c?.content === 'string') s += c.content;
    return s;
  }
  return String(r?.content ?? r?.text ?? '');
}

export interface AdvisorSetup {
  ai: ChatLike;
  advisor: string;
  /** The user's request as the advisor should read it (the current turn's text). */
  request: () => string;
  /** Host-supplied state the advisor reads NOW (mid-run): files, a diff, a summary. Capped. Default: none. */
  snapshot?: () => string | Promise<string>;
  limit?: number;
}

/** The `Advise` tool, plus `gate` for the file tools: the first write waits for one consult
 *  (a failed consult counts — it never blocks the work). */
export type Advisor = { tool: ModeTool & { description: string; parameters: Record<string, unknown> }; gate: <T extends ModeTool>(tools: T[]) => T[]; used: () => number };

export function makeAdvisor(o: AdvisorSetup): Advisor {
  let used = 0;
  const limit = o.limit ?? ADVISE_LIMIT;
  const tool = {
    name: 'Advise',
    description: `Ask a stronger model (${o.advisor}) for advice. It sees the user's request${o.snapshot ? ' and the current state' : ''}, not this conversation, so put everything it needs in \`question\`. Use it at most ${limit} times: once for the plan before you write, once to review the finished work.`,
    parameters: { type: 'object', properties: { question: { type: 'string', description: 'what you want advice on, with the context it needs' } }, required: ['question'] },
    async run(args: { question?: string }) {
      const question = String(args?.question ?? '').trim();
      if (!question) return 'No advice: the question is empty.';
      if (used >= limit) return `No advice: the limit of ${limit} calls is used. Continue with your own judgment.`;
      used++;
      let state = '';
      if (o.snapshot) {
        const s = String(await Promise.resolve(o.snapshot()).catch((err) => { log.warn('advisor snapshot failed:', String(err)); return ''; }) || '(empty)');
        state = `\n\n## Current state\n${s.length > SNAPSHOT_CAP ? `${s.slice(0, SNAPSHOT_CAP)}\n…(cut)` : s}`;
      }
      const review = used > 1 ? '' : '\n\n(When the work is complete, call Advise again for a review.)';
      try {
        const r = await o.ai.chat({
          model: o.advisor,
          messages: [
            { role: 'system', content: 'You advise a developer mid-task. Answer the question directly and concretely: the plan, the data shape, the risks, or the defects with their fix. Be brief. Do not write the whole solution.' },
            { role: 'user', content: `## User request\n${o.request()}${state}\n\n## Question\n${question}` },
          ],
          maxTokens: 2000,
        } as any);
        return ((await textOf(r)).trim() || 'No advice: the advisor returned nothing.') + review;
      } catch (err) {
        log.warn('advisor call failed:', String(err));
        return `No advice: the advisor call failed (${String((err as Error)?.message ?? err).slice(0, 120)}). Continue with your own judgment.`;
      }
    },
  };
  const gate = <T extends ModeTool>(tools: T[]): T[] =>
    tools.map((t) => !isFileWrite(t) ? t : { ...t, run: async (...a: any[]) => (used ? t.run(...a) : PLAN_FIRST) });
  return { tool, gate, used: () => used };
}

export type ChatUsage = { promptTokens: number; completionTokens: number; totalTokens: number; cacheCreationTokens?: number; cacheReadTokens?: number };

/** Meter the second model's calls: every call on the shared `ai` that names a model other than `main`
 *  (workers, the advisor). Streams count their LAST usage chunk, as the agent loop does. `model()` is
 *  the provider's resolved id of the last such call (captured right after it, before `main` overwrites it). */
export function meterWorkers<T extends ChatLike>(ai: T, main: string): { ai: T; usage: () => ChatUsage | undefined; model: () => string | undefined; reset: () => void } {
  let total: ChatUsage | undefined;
  let resolved: string | undefined;
  const seen = () => { resolved = (ai as { modelUsed?: string }).modelUsed ?? resolved; };
  const add = (u?: Partial<ChatUsage>) => {
    if (!u) return;
    const t = total ??= { promptTokens: 0, completionTokens: 0, totalTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };
    t.promptTokens += u.promptTokens ?? 0; t.completionTokens += u.completionTokens ?? 0; t.totalTokens += u.totalTokens ?? 0;
    t.cacheCreationTokens! += u.cacheCreationTokens ?? 0; t.cacheReadTokens! += u.cacheReadTokens ?? 0;
  };
  async function* tap(it: AsyncIterable<any>) {
    let last: Partial<ChatUsage> | undefined;
    try { for await (const c of it) { if (c?.usage) last = c.usage; yield c; } }
    finally { add(last); seen(); }
  }
  const chat = async (o: any) => {
    const r: any = await ai.chat(o);
    if (o?.model === main) return r;
    if (r && typeof r[Symbol.asyncIterator] === 'function') return tap(r);
    add(r?.usage); seen();
    return r;
  };
  return { ai: new Proxy(ai, { get: (t, k) => (k === 'chat' ? chat : Reflect.get(t, k)) }), usage: () => total && { ...total }, model: () => resolved, reset: () => { total = undefined; resolved = undefined; } };
}

/** Claude Code's NATIVE file-changing tools (its own loop runs them; agentx's tool wrappers never see the call). */
export const CC_WRITE_TOOLS = ['Write', 'Edit', 'MultiEdit', 'NotebookEdit'];

/**
 * The same two modes for a claude-code/* agent. Claude Code runs its OWN tool loop, so `delegateTools`/`gate`
 * (which wrap agentx tools) never fire there — Opus wrote with its native Write and had no worker tool.
 * - planner: native writes, Bash and native Task/Agent are `disallowedTools` (the SDK strips them); agentx's
 *   Task/TaskBatch reach it over the in-process bridge (ccBridge.ts), so it delegates to workers on the worker model.
 * - advised worker: `Advise` goes over the bridge; a PreToolUse hook denies a native write until one consult
 *   (`used()` > 0). The host's own `hooks` are kept.
 */
export function ccModeOptions(kind: 'delegate' | 'advisor' | undefined, hostHooks: Record<string, unknown[]> | undefined, used: () => number): { bridge: Set<string>; disallow: string[]; hooks?: Record<string, unknown[]> } {
  if (kind === 'delegate') return { bridge: new Set(['Task', 'TaskBatch']), disallow: [...CC_WRITE_TOOLS, 'Bash', 'Task', 'Agent'] };
  if (kind !== 'advisor') return { bridge: new Set(), disallow: [] };
  const gate = { matcher: CC_WRITE_TOOLS.join('|'), hooks: [async () => (used() ? {} : { hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: PLAN_FIRST.replace('`Advise`', '`mcp__agentx__Advise`') } })] };
  return { bridge: new Set(['Advise']), disallow: [], hooks: { ...hostHooks, PreToolUse: [...(hostHooks?.PreToolUse ?? []), gate] } };
}
